import React, { useState } from 'react';
import { Modal } from '@mantine/core';
import {
  IconBarbell,
  IconChartBar,
  IconFlame,
  IconTrophy,
} from '@tabler/icons-react';

import classes from './TrophyCase.module.css';

function categoryIcon(categoryFormatted) {
  switch (categoryFormatted) {
    case 'consistency':
      return IconFlame;
    case 'volume':
      return IconChartBar;
    case 'strength':
      return IconBarbell;
    default:
      return IconTrophy;
  }
}

function imageUrl(item) {
  if (item?.image?.filename_url) return item.image.filename_url;
  const assets = item?.achievement?.assets || item?.assets || [];
  const image = assets.find(
    (asset) => asset.variety === 1 || asset.variety_formatted === 'image',
  );
  return image?.filename_url || null;
}

function formatEarnedOn(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function trophyMeta(item) {
  const earned = !!item?.earned || !!item?.awarded_at || !!item?.achievement;
  return {
    earned,
    name: item?.name || item?.achievement?.name || 'Trophy',
    description: item?.description || item?.achievement?.description || '',
    category: item?.category_formatted || item?.achievement?.category_formatted,
    src: imageUrl(item),
    progress: item?.progress,
    awardCount: item?.award_count,
    earnedOn: earned ? formatEarnedOn(item?.awarded_at) : null,
  };
}

const FIREWORK_BURSTS = [
  { top: '12%', left: '18%', delay: '0ms' },
  { top: '10%', left: '82%', delay: '90ms' },
  { top: '48%', left: '8%', delay: '180ms' },
  { top: '44%', left: '92%', delay: '140ms' },
  { top: '8%', left: '50%', delay: '60ms' },
];

const FIREWORK_SPARKS = 12;

function TrophyFireworks() {
  return (
    <div className={classes.modalFireworks} aria-hidden>
      <span className={classes.fireworkFlashRing} />
      {FIREWORK_BURSTS.map((burst, burstIndex) => (
        <span
          key={`burst-${burstIndex}`}
          className={classes.fireworkBurst}
          style={{
            top: burst.top,
            left: burst.left,
          }}
        >
          {Array.from({ length: FIREWORK_SPARKS }, (_, sparkIndex) => {
            const angle = (360 / FIREWORK_SPARKS) * sparkIndex + burstIndex * 8;
            return (
              <span
                key={`ray-${burstIndex}-${sparkIndex}`}
                className={classes.fireworkRay}
                style={{ transform: `rotate(${angle}deg)` }}
              >
                <span
                  className={classes.fireworkSpark}
                  style={{ animationDelay: burst.delay }}
                />
              </span>
            );
          })}
        </span>
      ))}
    </div>
  );
}

function TrophyDetailModal({ item, opened, onClose }) {
  if (!item) return null;

  const meta = trophyMeta(item);
  const Icon = categoryIcon(meta.category);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size="md"
      radius="md"
      padding="lg"
      title={null}
      withCloseButton
      classNames={{
        content: classes.modalContent,
        body: classes.modalBody,
        header: classes.modalHeader,
      }}
    >
      <div className={classes.modalStage}>
        {opened ? <TrophyFireworks key={item.id || meta.name} /> : null}
        <div className={classes.modalGlow} aria-hidden />
        <div className={classes.modalArtWrap}>
          {meta.src ? (
            <img
              src={meta.src}
              alt={meta.name}
              className={classes.modalArtImage}
            />
          ) : (
            <div className={classes.modalArtFallback}>
              <Icon size={88} stroke={1.5} />
            </div>
          )}
          <span className={classes.modalShine} aria-hidden />
        </div>
      </div>
      <div className={classes.modalCopy}>
        <p className={classes.modalEyebrow}>
          {meta.earned ? 'Trophy' : 'Locked'}
        </p>
        <h2 className={classes.modalTitle}>{meta.name}</h2>
        {meta.description ? (
          <p className={classes.modalDesc}>{meta.description}</p>
        ) : null}
        {meta.earnedOn ? (
          <p className={classes.modalEarnedOn}>Earned on {meta.earnedOn}</p>
        ) : null}
        {!meta.earned && meta.progress?.target ? (
          <p className={classes.modalEarnedOn}>
            Progress {meta.progress.current ?? 0} / {meta.progress.target}
          </p>
        ) : null}
        {meta.earned && meta.awardCount > 1 ? (
          <p className={classes.modalEarnedOn}>Earned ×{meta.awardCount}</p>
        ) : null}
      </div>
    </Modal>
  );
}

export function TrophyBadgeTile({ item, compact = false, onOpen }) {
  const meta = trophyMeta(item);
  const Icon = categoryIcon(meta.category);
  const progressLabel =
    meta.progress && meta.progress.target
      ? `${meta.progress.current ?? 0} / ${meta.progress.target}`
      : null;
  const canOpen = meta.earned && typeof onOpen === 'function';

  let art = (
    <span className={classes.artMystery} aria-hidden>
      ?
    </span>
  );
  if (meta.earned && meta.src) {
    art = <img src={meta.src} alt="" className={classes.artImage} />;
  } else if (meta.earned) {
    art = <Icon size={compact ? 22 : 28} stroke={1.75} />;
  }

  const body = (
    <>
      <div
        className={[classes.art, !meta.earned ? classes.artLocked : '']
          .filter(Boolean)
          .join(' ')}
      >
        {art}
      </div>
      <div className={classes.tileBody}>
        <p className={classes.tileName}>{meta.name}</p>
        {!compact && meta.description ? (
          <p className={classes.tileDesc}>{meta.description}</p>
        ) : null}
        {meta.earnedOn ? (
          <p className={classes.tileEarnedOn}>Earned on {meta.earnedOn}</p>
        ) : null}
        {!meta.earned && progressLabel ? (
          <p className={classes.tileProgress}>{progressLabel}</p>
        ) : null}
        {meta.earned && meta.awardCount > 1 ? (
          <p className={classes.tileProgress}>×{meta.awardCount}</p>
        ) : null}
      </div>
    </>
  );

  const className = [
    classes.tile,
    meta.earned ? classes.tileEarned : classes.tileLocked,
    compact ? classes.tileCompact : '',
    canOpen ? classes.tileClickable : classes.tileStatic,
  ]
    .filter(Boolean)
    .join(' ');

  if (!canOpen) {
    return (
      <div className={className} aria-label={`${meta.name} (locked)`}>
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={className}
      onClick={() => onOpen(item)}
      aria-label={`View ${meta.name}`}
    >
      {body}
    </button>
  );
}

export function TrophyCaseGrid({ items = [], emptyMessage }) {
  const [selected, setSelected] = useState(null);

  if (!items.length) {
    return <p className={classes.empty}>{emptyMessage || 'No trophies yet.'}</p>;
  }

  return (
    <>
      <div className={classes.grid}>
        {items.map((item) => (
          <TrophyBadgeTile
            key={item.id || item.key || item.achievement_id}
            item={item}
            onOpen={setSelected}
          />
        ))}
      </div>
      <TrophyDetailModal
        item={selected}
        opened={Boolean(selected)}
        onClose={() => setSelected(null)}
      />
    </>
  );
}

export function TrophyCaseStrip({ awards = [], onViewAll, emptyMessage }) {
  const [selected, setSelected] = useState(null);

  return (
    <div className={classes.strip}>
      {awards.length === 0 ? (
        <p className={classes.empty}>
          {emptyMessage || 'Finish a workout to start your Trophy Case.'}
        </p>
      ) : (
        <div className={classes.stripRow}>
          {awards.map((award) => (
            <TrophyBadgeTile
              key={award.id}
              item={{
                ...award,
                earned: true,
                name: award.achievement?.name || award.name,
                description: award.achievement?.description,
                category_formatted: award.achievement?.category_formatted,
                image: award.achievement?.assets?.find(
                  (asset) =>
                    asset.variety === 1 || asset.variety_formatted === 'image',
                ),
              }}
              compact
              onOpen={setSelected}
            />
          ))}
        </div>
      )}
      {onViewAll ? (
        <button type="button" className={classes.viewAll} onClick={onViewAll}>
          View all
        </button>
      ) : null}
      <TrophyDetailModal
        item={selected}
        opened={Boolean(selected)}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

export function NewAwardsList({ awards = [] }) {
  if (!awards.length) return null;

  return (
    <div className={classes.newAwards}>
      <p className={classes.newAwardsEyebrow}>Added to Trophy Case</p>
      <ul className={classes.newAwardsList}>
        {awards.map((award) => (
          <li key={award.id || award.achievement_id}>
            {award.achievement?.name || award.name || 'Trophy'}
          </li>
        ))}
      </ul>
    </div>
  );
}
